import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Shield,
  Lock,
  Eye,
  Database,
  UserCheck,
  Globe,
  FileText,
  Mail
} from 'lucide-react';
import PageLayout, { PageHero } from './layout/PageLayout';
import SEO from './SEO';

const PrivacyPage = ({ onBack, onLogin, onRegister }) => {
  const lastUpdated = 'February 15, 2024';

  const sections = [
    {
      id: 'information-we-collect',
      icon: Database,
      title: 'Information We Collect',
      content: [
        {
          subtitle: 'Account Information',
          text: 'When you create an account, we collect your name, email address, and password. If you sign up through a third-party service, we may also receive information from that service.'
        },
        {
          subtitle: 'Usage Data',
          text: 'We automatically collect information about how you use our service, including the tests you create, execution logs, and feature usage patterns. This helps us improve our product.'
        },
        {
          subtitle: 'Device Information',
          text: 'We collect information about the device and browser you use to access our service, including IP address, browser type, and operating system.'
        }
      ]
    },
    {
      id: 'how-we-use',
      icon: Eye,
      title: 'How We Use Your Information',
      content: [
        {
          subtitle: 'Providing Our Service',
          text: 'We use your information to operate, maintain, and improve Lexa.run, including generating AI-powered tests, executing test runs, and providing customer support.'
        },
        {
          subtitle: 'Communication',
          text: 'We may send you service-related emails, including account verification, billing, and important updates. You can opt out of marketing communications at any time.'
        },
        {
          subtitle: 'Analytics & Improvement',
          text: 'We analyze usage patterns to understand how our service is used and to identify areas for improvement. This data is aggregated and anonymized where possible.'
        }
      ]
    },
    {
      id: 'data-sharing',
      icon: UserCheck,
      title: 'Data Sharing & Third Parties',
      content: [
        {
          subtitle: 'Service Providers',
          text: 'We share data with trusted third-party service providers who help us operate our service, including infrastructure, payment processing, and email delivery providers.'
        },
        {
          subtitle: 'Legal Requirements',
          text: 'We may disclose information if required by law, court order, or government request, or to protect our rights and the safety of our users.'
        },
        {
          subtitle: 'Business Transfers',
          text: 'In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.'
        }
      ]
    },
    {
      id: 'data-security',
      icon: Lock,
      title: 'Data Security',
      content: [
        {
          subtitle: 'Encryption',
          text: 'Passwords are hashed using bcrypt. Contact us for details about the security configuration applicable to your workspace.'
        },
        {
          subtitle: 'Access Controls',
          text: 'Workspace roles and project access controls govern access within the application.'
        },
        {
          subtitle: 'Infrastructure Security',
          text: 'Contact us to discuss infrastructure and security requirements before sharing sensitive information.'
        }
      ]
    },
    {
      id: 'your-rights',
      icon: FileText,
      title: 'Your Rights',
      content: [
        {
          subtitle: 'Access & Portability',
          text: 'You can access, download, and export your data at any time from your account settings. We provide data in standard formats for portability.'
        },
        {
          subtitle: 'Deletion',
          text: 'You can request deletion of your account and associated data. We will process deletion requests within 30 days, except where we\'re required to retain data by law.'
        },
        {
          subtitle: 'Correction',
          text: 'You can update or correct your personal information at any time through your account settings or by contacting our support team.'
        }
      ]
    },
    {
      id: 'international',
      icon: Globe,
      title: 'International Data Transfers',
      content: [
        {
          subtitle: 'Data Location',
          text: 'Your data is primarily stored in data centers in the United States. For EU users, we comply with GDPR requirements for international data transfers.'
        },
        {
          subtitle: 'Standard Contractual Clauses',
          text: 'We use EU-approved Standard Contractual Clauses and other appropriate safeguards when transferring data internationally.'
        }
      ]
    }
  ];

  return (
    <PageLayout onBack={onBack} onLogin={onLogin} onRegister={onRegister}>
      <SEO 
        title="Privacy Policy - Lexa.run"
        description="Learn how Lexa.run collects, uses, and protects your personal information. Our commitment to your privacy and data security."
      />

      <PageHero
        badge={<><Shield className="w-4 h-4" /> Privacy Policy</>}
        title="Your privacy"
        titleHighlight="matters to us"
        subtitle="We're committed to protecting your personal information and being transparent about how we collect, use, and share your data."
      />

      {/* Last Updated */}
      <section className="py-8 -mt-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <FileText className="w-4 h-4" />
            Last updated: {lastUpdated}
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-6 rounded-2xl border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-4">Contents</h3>
            <nav className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="flex items-center gap-2 text-gray-600 hover:text-purple-600 transition-colors py-1"
                >
                  <section.icon className="w-4 h-4" />
                  {section.title}
                </a>
              ))}
            </nav>
          </Card>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {sections.map((section) => (
            <div key={section.id} id={section.id} className="scroll-mt-32">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-xl bg-purple-100">
                  <section.icon className="w-6 h-6 text-purple-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
              </div>
              <div className="space-y-6 pl-16">
                {section.content.map((item, idx) => (
                  <div key={idx}>
                    <h3 className="font-semibold text-gray-900 mb-2">{item.subtitle}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500 text-white mb-6">
            <Mail className="w-8 h-8" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Questions about privacy?</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            If you have any questions about this Privacy Policy or our data practices, 
            please don't hesitate to contact our privacy team.
          </p>
          <a
            href="mailto:privacy@lexa.run"
            className="inline-flex items-center gap-2 text-purple-600 font-semibold hover:text-purple-700 transition-colors"
          >
            <Mail className="w-5 h-5" />
            privacy@lexa.run
          </a>
        </div>
      </section>
    </PageLayout>
  );
};

export default PrivacyPage;
